import {
    DomainInitPayload,
    NonStreamingCustomerOffersSMSResponse,
    OffersInfoResponse,
    OfferSMS,
} from '@sxm-de/dev-kits/web-sdk/types';
import {
    logErrorFromMicroservicesSdk,
    normalizeLangToLocaleForServiceCall,
    trackBusinessError,
} from '@sxm-de/dev-kits/web-sdk/utils';

interface GetNonStreamingCustomerOffersSMSFnPayload {
    radioId: string;
    programCode: string;
    province?: string;
}

export type GetNonStreamingCustomerOffersSMSFn = (
    payload: GetNonStreamingCustomerOffersSMSFnPayload,
) => Promise<NonStreamingCustomerOffersSMSResponse>;

export function getNonStreamingCustomerOffersSMS({
    dependencies,
    countryCodeDefault,
    locale,
}: DomainInitPayload): GetNonStreamingCustomerOffersSMSFn {
    const { appEventSdk, microservicesSdk } = dependencies;
    return async ({
        programCode,
        radioId,
        province,
    }: GetNonStreamingCustomerOffersSMSFnPayload) => {
        let planCode: string;
        let renewalPlanCode: string;
        let offers: OfferSMS[];
        let renewalOffers: OfferSMS[];
        let offerInfos: OffersInfoResponse;
        try {
            const offersResponse =
                await microservicesSdk.domains.offers.customer({
                    programCode,
                    radioId,
                    ...(province ? { province } : {}),
                    streaming: false,
                });
            offers = offersResponse.offers;
            planCode = offersResponse.offers[0]?.planCode;
        } catch (error: any) {
            if (error.errorType === 'BUSINESS') {
                trackBusinessError(appEventSdk, error);
            } else {
                logErrorFromMicroservicesSdk(error, dependencies);
            }
            throw new Error('SYSTEM');
        }
        try {
            const renewalOffersResponse =
                await microservicesSdk.domains.offers.renewal({
                    planCode,
                    radioId,
                    ...(province ? { province } : {}),
                    streaming: false,
                });
            renewalOffers = renewalOffersResponse.offers;
            renewalPlanCode = renewalOffersResponse.offers[0]?.planCode;
        } catch (error: any) {
            if (error.errorType === 'BUSINESS') {
                trackBusinessError(appEventSdk, error);
            } else {
                logErrorFromMicroservicesSdk(error, dependencies);
            }
            throw new Error('SYSTEM');
        }
        try {
            const request = {
                planCodes: [
                    {
                        leadOfferPlanCode: planCode,
                        renewalPlanCodes: [renewalPlanCode],
                    },
                ],
                locales: locale.supported.map((locale) =>
                    normalizeLangToLocaleForServiceCall(
                        locale,
                        countryCodeDefault,
                        undefined,
                    ),
                ),
                radioId,
                ...(province ? { province } : {}),
            };
            const offerInfoResponse =
                await microservicesSdk.domains.offers.info({
                    ...request,
                });
            offerInfos = {
                locales: offerInfoResponse.locales,
            };
        } catch (error: any) {
            if (error.errorType === 'BUSINESS') {
                trackBusinessError(appEventSdk, error);
            } else {
                logErrorFromMicroservicesSdk(error, dependencies);
            }
            throw new Error('SYSTEM');
        }
        return {
            offers,
            renewalOffers,
            offerInfos,
        };
    };
}
